import {
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useState,
} from "react"
import { createPortal } from "react-dom"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Badge } from "@/components/ui/badge"
import { KeyRound, Pencil, Plus, Search, UserCheck, UserX } from "lucide-react"
import { useAppSelector } from "@/reducers/hooks"
import { selectTabs, TAB_TYPES } from "@/reducers/tabsSlice"
import { Profile } from "@/reducers/profilesSlice"
import { XmppContext } from "@/stream/xmppClient"
import { extractVhostFromJid } from "@/jidUtils"
import { participantPhotoToSrc } from "@/components/AvatarImage"
import { retrieveAllUsersVCards } from "@/api/xeps/Users"
import Loader from "@/components/Loader"
import AddUserModal from "./AddUserModal"
import ResetPasswordModal from "./ResetPasswordModal"
import SetUserDisabledModal from "./SetUserDisabledModal"
import { useUserPermissions } from "@/hooks/useUserPermissions"
import { useTranslation } from "react-i18next"

const SYSTEM_USER_LOCALS = new Set([
  "slickshift",
  "communicator",
  "wizard",
  "assistant",
])

function getInitials(name: string, firstName?: string, lastName?: string) {
  if (firstName && lastName) {
    return `${firstName[0]}${lastName[0]}`.toUpperCase()
  }
  const parts = name.split(" ")
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase()
  }
  return name.slice(0, 2).toUpperCase()
}

function getDisplayName(
  name: string,
  firstName?: string,
  lastName?: string,
): string {
  if (firstName && lastName) {
    return `${firstName} ${lastName}`
  }
  return name
}

export function UserManagement() {
  const { t } = useTranslation()
  const { activeTab } = useAppSelector(selectTabs)
  const [isOpen, setIsOpen] = useState(false)
  const [users, setUsers] = useState<Profile[]>([])
  const [loading, setLoading] = useState(false)
  const [isAddModalOpen, setIsAddModalOpen] = useState(false)
  const [editingUser, setEditingUser] = useState<Profile | null>(null)
  const [resettingPasswordUser, setResettingPasswordUser] =
    useState<Profile | null>(null)
  const [disablingUser, setDisablingUser] = useState<{
    user: Profile
    enabling: boolean
  } | null>(null)
  const [filter, setFilter] = useState("")

  const filteredUsers = useMemo(() => {
    const q = filter.toLowerCase().trim()
    if (!q) return users
    return users.filter((u) => {
      const username = u.jid.split("@")[0] ?? ""
      return (
        (u.firstName ?? "").toLowerCase().includes(q) ||
        (u.lastName ?? "").toLowerCase().includes(q) ||
        username.toLowerCase().includes(q) ||
        (u.phone ?? "").toLowerCase().includes(q)
      )
    })
  }, [users, filter])
  const [fetchKey, setFetchKey] = useState(0)
  const { client } = useContext(XmppContext)
  const { hasPermission } = useUserPermissions()

  const handleUserAdded = useCallback(() => {
    setFetchKey((k) => k + 1)
  }, [])

  useLayoutEffect(() => {
    setIsOpen(activeTab === TAB_TYPES.USER_MANAGEMENT)
  }, [activeTab])

  useEffect(() => {
    if (!isOpen) return

    setLoading(true)
    const myVhost = client?.jid?.toString()
      ? extractVhostFromJid(client.jid.toString())
      : null

    retrieveAllUsersVCards(window.XMPP!)
      .then((allUsers) => {
        const filtered = allUsers
          .filter((u): u is Profile => {
            if (!u) return false
            const local = u.jid.split("@")[0]?.toLowerCase()
            if (local && SYSTEM_USER_LOCALS.has(local)) return false
            if (u.role !== "office") return false
            if (myVhost) {
              const userVhost = extractVhostFromJid(u.jid)
              if (userVhost !== myVhost) return false
            }
            return true
          })
          .sort((a, b) => {
            const lastA = (a.lastName ?? a.name).toLowerCase()
            const lastB = (b.lastName ?? b.name).toLowerCase()
            return lastA.localeCompare(lastB)
          })
        setUsers(filtered)
      })
      .catch((err) => {
        console.warn("Failed to fetch users", err)
      })
      .finally(() => {
        setLoading(false)
      })
  }, [isOpen, client, fetchKey])

  return (
    <>
      <AddUserModal
        isOpen={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onSuccess={handleUserAdded}
      />
      <AddUserModal
        isOpen={editingUser !== null}
        onClose={() => setEditingUser(null)}
        onSuccess={handleUserAdded}
        user={editingUser ?? undefined}
      />
      {resettingPasswordUser && (
        <ResetPasswordModal
          isOpen
          onClose={() => setResettingPasswordUser(null)}
          user={resettingPasswordUser}
        />
      )}
      {disablingUser && (
        <SetUserDisabledModal
          isOpen
          onClose={() => setDisablingUser(null)}
          user={disablingUser.user}
          enabling={disablingUser.enabling}
          onSuccess={handleUserAdded}
        />
      )}
      {isOpen &&
        createPortal(
          <div className="fixed inset-0 left-[49px] z-50 flex w-[100vw-48px] items-center justify-center rounded-md bg-background">
            <div className="relative h-full w-full">
              <div className="h-full w-full overflow-auto">
                <div className="p-6">
                  <div className="mb-6 flex items-center justify-between">
                    <div>
                      <h1 className="text-2xl font-semibold">
                        {t("fleet.users.title")}
                      </h1>
                      <p className="text-sm text-muted-foreground">
                        {t("fleet.users.subtitle")}
                      </p>
                    </div>
                    {hasPermission("users.create") && (
                      <Button
                        onClick={() => setIsAddModalOpen(true)}
                        className="bg-blue-600 hover:bg-blue-700"
                      >
                        <Plus className="mr-2 h-4 w-4" />
                        {t("fleet.users.addUser")}
                      </Button>
                    )}
                  </div>

                  <div className="relative mb-4">
                    <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                    <Input
                      placeholder={t("fleet.users.filter")}
                      value={filter}
                      onChange={(e) => setFilter(e.target.value)}
                      autoComplete="off"
                      className="pl-9"
                    />
                  </div>

                  {loading ? (
                    <div className="flex h-40 items-center justify-center">
                      <Loader />
                    </div>
                  ) : (
                    <div className="rounded-md border">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead className="w-[50px]"></TableHead>
                            <TableHead>{t("fleet.users.name")}</TableHead>
                            <TableHead>{t("fleet.users.login")}</TableHead>
                            <TableHead>{t("fleet.users.phone")}</TableHead>
                            <TableHead className="w-[60px]"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {filteredUsers.length === 0 ? (
                            <TableRow>
                              <TableCell
                                colSpan={4}
                                className="h-24 text-center text-muted-foreground"
                              >
                                {t("fleet.users.empty")}
                              </TableCell>
                            </TableRow>
                          ) : (
                            filteredUsers.map((user) => {
                              const photoSrc = participantPhotoToSrc(user.photo)
                              const displayName = getDisplayName(
                                user.name,
                                user.firstName,
                                user.lastName,
                              )
                              const initials = getInitials(
                                user.name,
                                user.firstName,
                                user.lastName,
                              )
                              return (
                                <TableRow
                                  key={user.jid}
                                  className={user.disabled ? "opacity-60" : ""}
                                >
                                  <TableCell>
                                    <Avatar className="h-8 w-8">
                                      {photoSrc && (
                                        <AvatarImage src={photoSrc} />
                                      )}
                                      <AvatarFallback className="text-xs">
                                        {initials}
                                      </AvatarFallback>
                                    </Avatar>
                                  </TableCell>
                                  <TableCell className="font-medium">
                                    <div className="flex items-center gap-2">
                                      {displayName}
                                      {user.disabled && (
                                        <Badge
                                          variant="secondary"
                                          className="border-red-300 bg-red-100 text-xs text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-400"
                                        >
                                          {t("fleet.users.disabled")}
                                        </Badge>
                                      )}
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-muted-foreground">
                                    {user.jid}
                                  </TableCell>
                                  <TableCell>{user.phone || "-"}</TableCell>
                                  <TableCell>
                                    <div className="flex gap-1">
                                      {hasPermission("users.update") &&
                                        (user.disabled ? (
                                          <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() =>
                                              setDisablingUser({
                                                user,
                                                enabling: true,
                                              })
                                            }
                                            title={t(
                                              "fleet.users.reEnableUser",
                                            )}
                                            className="text-green-600 hover:text-green-700"
                                          >
                                            <UserCheck className="h-4 w-4" />
                                          </Button>
                                        ) : (
                                          <>
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              onClick={() =>
                                                setEditingUser(user)
                                              }
                                              title={t("fleet.users.editUser")}
                                            >
                                              <Pencil className="h-4 w-4" />
                                            </Button>
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              onClick={() =>
                                                setResettingPasswordUser(user)
                                              }
                                              title={t(
                                                "fleet.users.resetPassword",
                                              )}
                                            >
                                              <KeyRound className="h-4 w-4" />
                                            </Button>
                                            <Button
                                              variant="ghost"
                                              size="sm"
                                              onClick={() =>
                                                setDisablingUser({
                                                  user,
                                                  enabling: false,
                                                })
                                              }
                                              title={t(
                                                "fleet.users.disableUser",
                                              )}
                                              className="text-red-600 hover:text-red-700"
                                            >
                                              <UserX className="h-4 w-4" />
                                            </Button>
                                          </>
                                        ))}
                                    </div>
                                  </TableCell>
                                </TableRow>
                              )
                            })
                          )}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  )
}

export default UserManagement
